import { useCallback } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import type { GordianSeat } from "@b2bportal/air-seats-api";
import type { HopperSeatTotal } from "@checkout/states/Products/Flight/states/SeatSelection/context";
import { trackEvent } from "@hopper-b2b/api";
import {
  FlightPassengerSelectorsV2,
  FlightSelectors,
  ReviewSelectors,
  type SeatEvents,
  SeatEventTypes,
  SeatSelectionErrorModal,
  SeatSelectors,
  useCheckoutSend,
  useCheckoutStateSelector as useSelector,
  useSeatSelectionErrorConfig,
} from "@hopper-b2b/checkout";
import { useI18nContext } from "@hopper-b2b/i18n";
import { REQUESTED_SEATMAP, type GordianSeatSegment } from "@hopper-b2b/types";
import { ErrorModal, Spinner } from "@hopper-b2b/ui";

import Error from "../../../../assets/client/error.svg";
import { useNubankFeatureFlagsContext } from "../../../../utils/featureFlagsContext";
import { PATH_FLIGHTS_SEARCH } from "../../../../utils/urlPaths";
import { SelectedSeats } from "./components";
import styles from "./styles.module.scss";

export const ConnectedSeatSelection = () => {
  const send = useCheckoutSend<SeatEvents>();
  const navigate = useNavigate();

  const tripDetails = useSelector(FlightSelectors.getSelectedTripDetailsParent);
  const passengers = useSelector(
    FlightPassengerSelectorsV2.getAllSelectedUserPassengersParent
  );

  const isLoading = useSelector(SeatSelectors.getIsSeatSelectionLoading);
  const selectedSeats = useSelector(SeatSelectors.getSelectedSeats);
  const selectedSeatSegments = useSelector(
    SeatSelectors.getSelectedSeatSegments
  );
  const seatSlices = useSelector(SeatSelectors.getSeatSlices);
  const cheapestSeat = useSelector(SeatSelectors.getCheapestSeat);
  const seatMapHtml = useSelector(SeatSelectors.getSeatMapHtml);
  const totalSeatPricing = useSelector(SeatSelectors.getSeatTotalPricing);
  const airports = useSelector(FlightSelectors.getAirportMap);
  const editSeatsFromReview = useSelector(
    ReviewSelectors.getEditSeatsFromReview
  );

  const goBack = useCallback(() => navigate(-1), [navigate]);
  const goNext = useCallback(() => {
    send(SeatEventTypes.NEXT);
  }, [send]);

  const setSelectedSeats = useCallback(
    (
      seats: GordianSeat[],
      seatSegments: GordianSeatSegment[],
      selectedSeatTotal?: HopperSeatTotal
    ) => {
      send({
        type: SeatEventTypes.SELECT_SEATS,
        seats,
        seatSegments,
        selectedSeatTotal,
      });
      if (editSeatsFromReview) {
        goNext();
      }
    },
    [editSeatsFromReview, goNext, send]
  );

  const handleSelectSeats = useCallback(() => {
    if (selectedSeats.length > 0) {
      goNext();
    } else {
      trackEvent({
        eventName: REQUESTED_SEATMAP,
        properties: {},
      });
    }
  }, [goNext, selectedSeats.length]);

  return (
    <>
      {isLoading ? (
        <div className={styles.SeatSelectionLoading}>
          <Spinner />
        </div>
      ) : (
        <SelectedSeats
          tripDetails={tripDetails}
          seatSlices={seatSlices}
          airports={airports}
          cheapestSeat={cheapestSeat}
          selectedSeats={selectedSeats}
          passengers={passengers}
          totalSeatPricing={totalSeatPricing}
          seatMapHtml={seatMapHtml}
          selectedSeatSegments={selectedSeatSegments}
          fonts={['<link rel="stylesheet" href="../../../../fonts.scss">']}
          fontFamilies={['"NuSansText"']}
          onNext={goNext}
          onBack={goBack}
          onSelectSeats={handleSelectSeats}
          onSeatsSelected={setSelectedSeats}
        />
      )}
      <ConnectedErrorModal />
    </>
  );
};

/**
 * New error modal using centralized config
 */
const CentralizedErrorModal = () => {
  const navigate = useNavigate();
  const send = useCheckoutSend<SeatEvents>();
  const open = useSelector(SeatSelectors.getSeatSelectionErrorOpen);
  const contactError = useSelector(SeatSelectors.getSeatSelectionError);

  const { getSeatSelectionErrorProps, getDefaultProps } =
    useSeatSelectionErrorConfig({
      handlers: {
        navigateToFlightSearch: () => {
          send(SeatEventTypes.CLEAR_SEAT_ERROR);
          navigate(`/${PATH_FLIGHTS_SEARCH}`);
        },
        continueWithoutSeats: () => {
          send(SeatEventTypes.CLEAR_SEAT_ERROR);
          send(SeatEventTypes.NEXT);
        },
        clearError: () => {
          send(SeatEventTypes.CLEAR_SEAT_ERROR);
        },
      },
    });

  const errorModalProps = contactError?.type
    ? getSeatSelectionErrorProps(contactError.type)
    : getDefaultProps();

  return <ErrorModal open={open} {...errorModalProps} image={Error} />;
};

/**
 * Legacy error modal (original implementation)
 */
const LegacyErrorModal = () => {
  const { t } = useI18nContext();
  const navigate = useNavigate();
  const send = useCheckoutSend<SeatEvents>();
  const open = useSelector(SeatSelectors.getSeatSelectionErrorOpen);
  const contactError = useSelector(SeatSelectors.getSeatSelectionError);

  const clearError = () => send(SeatEventTypes.CLEAR_SEAT_ERROR);
  const goNext = () => send(SeatEventTypes.NEXT);

  const continueWithoutSeats = () => {
    clearError();
    goNext();
  };

  const searchAgain = () => {
    clearError();
    navigate(`/${PATH_FLIGHTS_SEARCH}`);
  };

  const getErrorModalProps = (type?: SeatSelectionErrorModal) => {
    switch (type) {
      case SeatSelectionErrorModal.BrokenCart:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };

      case SeatSelectionErrorModal.FailedAddToCart:
        return {
          title: t("seats.errorFailedAddToCartTitle"),
          subtitle: t("seats.errorFailedAddToCartSubtitle"),
          primaryButtonText: t("seats.continueWithoutSeats"),
          primaryOnClick: continueWithoutSeats,
          secondaryButtonText: t("checkoutError.searchAgain"),
          secondaryOnClick: searchAgain,
        };

      case SeatSelectionErrorModal.GenericError:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
      default:
        return {
          title: t("checkoutError.genericErrorTitle"),
          subtitle: t("checkoutError.genericErrorSubtitle"),
          primaryButtonText: t("checkoutError.searchAgain"),
          primaryOnClick: searchAgain,
        };
    }
  };
  const errorModalProps = getErrorModalProps(contactError?.type);

  return <ErrorModal open={open} {...errorModalProps} image={Error} />;
};

/**
 * Error modal component that switches between legacy and centralized config
 * based on feature flag
 */
export const ConnectedErrorModal = () => {
  const { enableCentralizedErrorConfig } = useNubankFeatureFlagsContext();

  if (enableCentralizedErrorConfig) {
    return <CentralizedErrorModal />;
  }

  return <LegacyErrorModal />;
};
